import React from "react";
import { Link } from "react-router-dom";

/**
 * ANATRADEX Academy — Extra tracks (Psychology, Risk, Journal).
 * Following the same shape as CHARTISTE_ARTICLES: {slug, track, level, minutes, order, title, summary, content}.
 */

// -----------------------------------------------------------------------------
// Track: Psychology
// -----------------------------------------------------------------------------
export const PSYCHOLOGY_ARTICLES = [
  {
    slug: "biais-cognitifs-du-trader",
    track: "psychology",
    level: "Débutant",
    minutes: 6,
    order: 1,
    title: "Les 7 biais cognitifs qui ruinent les traders",
    summary: "Confirmation, disposition, ancrage… Reconnaître ses biais est la moitié du travail.",
    content: (
      <>
        <p>Notre cerveau n&apos;a pas évolué pour trader. Il a évolué pour survivre dans la savane. Résultat : il vous saborde en permanence sur les marchés.</p>
        <h3>1. Biais de confirmation</h3>
        <p>Vous êtes long EURUSD ? Vous ne lisez que les articles bullish. Solution : forcez-vous à lire l&apos;argumentaire opposé <b>avant</b> d&apos;entrer, pas après.</p>
        <h3>2. Effet de disposition</h3>
        <p>Prendre les gains trop vite, laisser courir les pertes. C&apos;est mathématiquement suicidaire (R:R inversé). Discipline : le stop est <em>sacré</em>, il ne se déplace jamais dans le mauvais sens.</p>
        <h3>3. Ancrage sur le prix d&apos;achat</h3>
        <p>« Je vends dès qu&apos;il revient à mon prix d&apos;entrée. » Le marché s&apos;en fout de votre prix. Décidez de sortir selon la <b>structure</b>, pas l&apos;ancrage.</p>
        <h3>4. Sunk cost fallacy</h3>
        <p>« J&apos;ai déjà perdu 500 €, autant tenir… » Non. Chaque nouvelle décision est indépendante. Vos pertes passées sont irrécupérables. Coupez.</p>
        <h3>5. Overconfidence après une série gagnante</h3>
        <p>3 trades gagnants d&apos;affilée = « je suis un dieu ». Statistiquement, c&apos;est du hasard. Ne montez <em>jamais</em> la taille après une série chaude.</p>
        <h3>6. Aversion à la perte (loss aversion)</h3>
        <p>Perdre 100 € fait 2× plus mal que gagner 100 € ne fait plaisir. Résultat : trades size trop petits par peur. Reproduisez le sizing sur papier pendant 30 trades pour vous habituer.</p>
        <h3>7. Recency bias</h3>
        <p>Vous répliquez la stratégie qui a marché la dernière semaine, même en régime changé. Consultez le <Link to="/app/allocation" className="text-accent-blue underline">détecteur de régime</Link> avant chaque session.</p>
        <p><b>Retenir :</b> vous ne pouvez pas éliminer ces biais. Vous ne pouvez que les <em>reconnaître</em> et automatiser vos décisions autant que possible.</p>
      </>
    ),
  },
  {
    slug: "gerer-le-tilt-et-le-revenge-trading",
    track: "psychology",
    level: "Semi-pro",
    minutes: 6,
    order: 2,
    title: "Gérer le tilt et éviter le revenge trading",
    summary: "Après un stop-loss, votre cerveau veut « se refaire ». C'est le moment le plus dangereux.",
    content: (
      <>
        <p>Le <b>tilt</b> (concept issu du poker) désigne l&apos;état émotionnel dégradé qui suit une perte. Le <b>revenge trading</b> est sa manifestation la plus destructrice : entrer immédiatement dans un trade agressif pour « récupérer ».</p>
        <h3>Les symptômes du tilt</h3>
        <ul>
          <li>Envie compulsive d&apos;ouvrir la plateforme.</li>
          <li>Justifications rapides (« ça DOIT rebondir »).</li>
          <li>Augmentation de la taille sans raison analytique.</li>
          <li>Focus sur la P&L de la session au lieu de la stratégie.</li>
        </ul>
        <h3>Protocole anti-tilt en 3 règles</h3>
        <ol>
          <li><b>Règle des 15 minutes</b> : après <em>tout</em> stop-loss, on ferme la plateforme 15 min minimum. Non négociable.</li>
          <li><b>Kill-switch quotidien</b> : au-delà de 2 pertes consécutives, vous stoppez pour la journée. ANATRADEX inclut un <Link to="/app/risk" className="text-accent-blue underline">Kill-Switch automatique</Link> qui bloque la plateforme si le seuil est atteint.</li>
          <li><b>Journal de session</b> : notez émotion + raison objective de chaque trade dans le <Link to="/app/profile" className="text-accent-blue underline">Journal</Link>. Les schémas émotionnels apparaissent en 2-3 semaines.</li>
        </ol>
        <h3>Pourquoi c&apos;est mathématique</h3>
        <p>Un système avec 50% winrate + R:R 2:1 est profitable à long terme. Mais avec <b>3 trades tilt sur 20</b> qui violent la size, l&apos;espérance passe négative. Le tilt tue même les bons systèmes.</p>
        <h3>Automatisation = discipline sans effort</h3>
        <p>La meilleure arme contre le tilt est de <b>coder vos règles</b>. Si votre système décide, vous n&apos;avez plus à résister à l&apos;émotion. C&apos;est tout l&apos;objectif de la couche systématique ANATRADEX.</p>
      </>
    ),
  },
  {
    slug: "routine-quotidienne-trader",
    track: "psychology",
    level: "Semi-pro",
    minutes: 5,
    order: 3,
    title: "La routine quotidienne du trader professionnel",
    summary: "Avant la session, pendant, après. Une routine bornée = performance stable.",
    content: (
      <>
        <p>Les trades profitables commencent 1h <em>avant</em> l&apos;ouverture et se terminent 30 min <em>après</em> la fermeture. Voici la routine standard des prop-traders sérieux.</p>
        <h3>Pré-session (30-60 min)</h3>
        <ol>
          <li>Contrôle du calendrier économique (Investing.com, ForexFactory ou le panneau <em>Events</em> de ANATRADEX).</li>
          <li>Revue des niveaux D1/H4 : HOD/LOD précédent, prochaine résistance, kill-zones actives.</li>
          <li>Vérification de votre P&L hebdo — êtes-vous sous ou au-dessus de votre daily loss limit ?</li>
          <li>Plan écrit : 3 setups maximum, chacun avec entrée / stop / cible / justification.</li>
        </ol>
        <h3>Pendant la session</h3>
        <ol>
          <li>Un trade = un setup planifié. Pas d&apos;improvisation.</li>
          <li>Après chaque trade : capture d&apos;écran + note de 2 lignes dans le journal.</li>
          <li>Pause de 10 min toutes les 90 min. Le cerveau ne peut pas maintenir la concentration au-delà.</li>
          <li>Kill-switch = 2 stops = fin de journée.</li>
        </ol>
        <h3>Post-session (15-30 min)</h3>
        <ol>
          <li>Revue des trades ouverts + fermés. Ce qui a marché, ce qui n&apos;a pas marché.</li>
          <li>Update du journal : émotions, régime marché, qualité d&apos;exécution.</li>
          <li>Le vendredi : synthèse hebdo. Sharpe, winrate, max DD, meilleur/pire trade.</li>
        </ol>
        <h3>Éléments hygiène</h3>
        <p>Sommeil {">"} 7h. Pas d&apos;alcool avant une session importante. Sport 3× par semaine. Ce sont des <b>edge multipliers</b>, pas des détails.</p>
      </>
    ),
  },
  {
    slug: "gestion-de-l-echec-et-drawdown",
    track: "psychology",
    level: "Pro",
    minutes: 7,
    order: 4,
    title: "Traverser un drawdown sans exploser",
    summary: "Tous les systèmes traversent des DD. Ce qui vous distingue, c'est comment vous les traversez.",
    content: (
      <>
        <p>Un drawdown (DD) de 15-25% est <b>statistiquement normal</b>, même pour une excellente stratégie. La question n&apos;est pas si vous en traverserez, mais comment vous réagirez.</p>
        <h3>Les 3 phases psychologiques d&apos;un DD</h3>
        <ol>
          <li><b>Déni</b> (-5%) : « c&apos;est du bruit, ça reviendra ».</li>
          <li><b>Doute</b> (-10%) : « peut-être que ma stratégie ne marche plus ».</li>
          <li><b>Capitulation</b> (-15% à -25%) : envie d&apos;abandonner, de doubler la taille, ou de switcher de stratégie. <b>C&apos;est ici que 90% des traders détruisent leur edge.</b></li>
        </ol>
        <h3>Ce que font les pros</h3>
        <ol>
          <li><b>Réduire la taille dès -8%</b>. Passer de 1% à 0,5% par trade. Vous survivez plus longtemps, vous laissez la stat revenir.</li>
          <li><b>Refuser de changer la stratégie</b> tant que le DD n&apos;a pas dépassé le max historique + 30%. En dessous = bruit.</li>
          <li><b>Consulter le journal</b>. Les erreurs sont-elles liées à la stratégie ou à l&apos;exécution ? 80% du temps c&apos;est de l&apos;exécution (tilt, taille, timing).</li>
          <li><b>Se retirer temporairement</b> si les émotions dominent. 5 jours off {">"}  40 jours de pertes.</li>
        </ol>
        <h3>Le calcul de récupération</h3>
        <p>Un DD de -20% requiert <b>+25% de gain</b> pour revenir à l&apos;équilibre. -50% requiert <b>+100%</b>. C&apos;est asymétrique. <em>D&apos;où l&apos;importance de couper les DD tôt.</em></p>
        <h3>Le vrai signal d&apos;alerte</h3>
        <p>Ce n&apos;est pas la profondeur du DD, c&apos;est sa <b>durée</b>. Un DD de 15% en 3 semaines = normal. Un DD de 15% en 6 mois qui refuse de sortir = la stratégie est probablement cassée (régime différent, edge dilué). Là, il faut arrêter et re-backtester.</p>
      </>
    ),
  },
];

// -----------------------------------------------------------------------------
// Track: Advanced Risk Management
// -----------------------------------------------------------------------------
export const RISK_ARTICLES = [
  {
    slug: "risque-fixe-vs-risque-variable",
    track: "risk",
    level: "Débutant",
    minutes: 5,
    order: 1,
    title: "Risque fixe vs variable : quel modèle pour vous ?",
    summary: "1% par trade, Kelly, martingale (à éviter). Les 4 modèles de sizing expliqués.",
    content: (
      <>
        <p>Combien risquer par trade est la <b>décision la plus importante</b> de votre carrière. Voici les modèles.</p>
        <h3>1. Risque fixe en $ (retail débutant)</h3>
        <p>« Je risque 100 € par trade quoi qu&apos;il arrive. » Simple, mais ne s&apos;adapte pas à la croissance du compte. Bon pour démarrer.</p>
        <h3>2. Risque fixe en % (standard institutionnel)</h3>
        <p>1% du capital par trade. Vous croissez avec le compte, vous décroissez avec les pertes. <b>Standard de facto</b> chez les hedge funds retail-facing.</p>
        <h3>3. Kelly Fractionnaire (avancé)</h3>
        <p>Calcule le % optimal selon votre winrate + R:R historiques. Standard : <b>Kelly/4 ou Kelly/5</b>. Voir article <Link to="/academy/kelly-fractionnaire-position-sizing" className="text-accent-blue underline">dédié</Link>.</p>
        <h3>4. Volatility-adjusted sizing (élite)</h3>
        <p>Sizing basé sur l&apos;ATR ou la vol implicite. En période calme : plus gros. En période volatile : plus petit. ANATRADEX calcule ça automatiquement dans le <Link to="/app/sizer" className="text-accent-blue underline">Position Sizer</Link>.</p>
        <h3>À éviter absolument</h3>
        <ul>
          <li><b>Martingale</b> : doubler la taille après une perte. Ruine mathématiquement garantie.</li>
          <li><b>Anti-martingale agressive</b> : doubler la taille après un gain. Vous rendez tout le vendredi.</li>
          <li><b>All-in sur « conviction »</b> : chaque trade doit être fongible.</li>
        </ul>
        <h3>Recommandation par expérience</h3>
        <p>Débutant (moins de 1000 trades) : <b>0,5% par trade</b>. Semi-pro : <b>1% par trade</b>. Prop firm : <b>0,3-0,5%</b>. Portfolio manager LP : Kelly/5 max.</p>
      </>
    ),
  },
  {
    slug: "correlations-portfolio",
    track: "risk",
    level: "Semi-pro",
    minutes: 7,
    order: 2,
    title: "Corrélations : le vrai risque caché du multi-actifs",
    summary: "EURUSD + GBPUSD + AUDUSD long = 3× la même exposition. Détecter et neutraliser.",
    content: (
      <>
        <p>Vous risquez 1% sur EURUSD, 1% sur GBPUSD, 1% sur AUDUSD. Vous pensez risquer 3%. En réalité vous risquez <b>2,5-2,8%</b> car ces paires sont corrélées à 0,7-0,85.</p>
        <h3>Corrélation, en pratique</h3>
        <p>Deux actifs corrélés à +1 bougent identiquement. À -1, ils bougent en miroir. À 0, ils sont indépendants. Les corrélations forex typiques :</p>
        <ul>
          <li>EURUSD / GBPUSD : +0,75 à +0,85</li>
          <li>EURUSD / USDCHF : -0,90 à -0,95 (miroir presque parfait)</li>
          <li>AUDUSD / Gold : +0,60 à +0,75</li>
          <li>USDJPY / NAS100 : +0,50 à +0,70 (risk-on)</li>
          <li>BTCUSD / NAS100 : +0,50 à +0,70 (depuis 2020)</li>
        </ul>
        <h3>Le vrai risque portfolio</h3>
        <p>Formule : <code>Risk_total² = Σ risk_i² + 2 × Σ ρ_ij × risk_i × risk_j</code></p>
        <p>Trois positions corrélées à 0,8 avec 1% chacune = <b>2,68% de risque effectif</b>, pas 3%. À l&apos;inverse, une short EURUSD + long USDCHF = <b>0,32% de risque effectif</b> car les corrélations s&apos;annulent.</p>
        <h3>Règles pratiques</h3>
        <ol>
          <li>Ne prenez jamais {">"} 3 positions long-USD simultanées.</li>
          <li>Vérifiez la corrélation crypto/tech avant de doubler l&apos;exposition risk-on.</li>
          <li>Utilisez la commande <code>POST /api/allocation/kelly-multi</code> qui prend une matrice de corrélations et retourne les tailles ajustées.</li>
          <li>Recalculez les corrélations tous les 30 jours — elles bougent (crise 2020 : tout est devenu corrélé à 0,9+).</li>
        </ol>
        <h3>Les régimes de corrélation</h3>
        <p>En période « risk-off », <b>tout devient corrélé</b>. Diversification apparente ≠ diversification réelle. C&apos;est pour ça que les fonds hedgent avec de la volatilité (VIX) et pas juste des actifs classiques.</p>
      </>
    ),
  },
  {
    slug: "stop-loss-avances",
    track: "risk",
    level: "Semi-pro",
    minutes: 6,
    order: 3,
    title: "Stop-loss avancés : ATR, structure, trailing",
    summary: "Le stop fixe est primitif. Voici les 4 techniques utilisées en desk institutionnel.",
    content: (
      <>
        <p>Un stop-loss n&apos;est pas juste « un chiffre en dessous de l&apos;entrée ». C&apos;est la <b>base de tout votre système de gestion du risque</b>.</p>
        <h3>1. Stop ATR (Average True Range)</h3>
        <p>Placez le stop à <b>1,5-2× ATR</b> de l&apos;entrée. L&apos;ATR mesure la volatilité récente. Résultat : le stop s&apos;adapte automatiquement au marché. En période calme, stop serré ; en période volatile, stop large. <b>C&apos;est le standard des prop-firms</b>.</p>
        <h3>2. Stop structurel</h3>
        <p>Placez le stop juste au-delà du dernier swing high/low. Si le marché casse ce niveau, votre thèse est invalidée. <b>Cela offre le meilleur R:R</b> car le stop est justifié logiquement, pas arithmétiquement. Utilisez l&apos;indicateur <em>Swing High/Low</em> de ANATRADEX.</p>
        <h3>3. Stop temporel</h3>
        <p>« Si le trade ne fonctionne pas dans les 24h/3 bougies, je sors, même à breakeven. » Force la discipline et évite les capitaux immobilisés. Combinez avec un stop prix.</p>
        <h3>4. Trailing stop (poursuite)</h3>
        <p>Le stop suit le prix avec une distance fixe (ATR ou %). Sur un trade gagnant, il se déplace uniquement dans votre sens. Verrouille les gains sans limiter le potentiel. <b>3 variantes</b> :</p>
        <ul>
          <li>Chandelier Stop : trail à 3× ATR du plus haut atteint</li>
          <li>Parabolic SAR : accélère au fur et à mesure du mouvement</li>
          <li>Swing-based trail : passe sous chaque nouveau swing low en tendance haussière</li>
        </ul>
        <h3>Ce qu&apos;il faut éviter</h3>
        <ul>
          <li>Stop trop serré = tous les trades sont stopés par le bruit.</li>
          <li>Stop trop large = R:R détruit.</li>
          <li>Déplacer le stop <em>contre</em> vous (jamais). Le déplacer <em>en votre faveur</em> (breakeven / trailing) est OK.</li>
          <li>Pas de stop = folie pure. Un flash-crash suffit à détruire un compte.</li>
        </ul>
      </>
    ),
  },
  {
    slug: "black-swan-tail-risk",
    track: "risk",
    level: "Pro",
    minutes: 7,
    order: 4,
    title: "Black swans et tail risk : se protéger de l'imprévisible",
    summary: "SNB 2015, Covid 2020, GameStop 2021. Comment vos gains d'une décennie disparaissent en 1 heure.",
    content: (
      <>
        <p>Les « black swans » (Taleb) sont des événements très rares mais catastrophiques : SNB décrochage du CHF 2015, Covid mars 2020, LUNA/UST 2022, Silicon Valley Bank 2023. Chacun a ruiné des milliers de comptes en quelques heures.</p>
        <h3>Pourquoi la statistique classique ment</h3>
        <p>Les rendements financiers ne sont <b>PAS gaussiens</b>. Ils ont des <em>fat tails</em> (kurtosis {">"} 3). Un événement à « 6-sigma » (censé arriver 1× par million d&apos;années sous une gaussienne) arrive en réalité 1× par 10 ans. La théorie Modern Portfolio Theory sous-estime le risque tail de 10 à 100×.</p>
        <h3>Les 5 protections tail-risk</h3>
        <ol>
          <li><b>Hard cap position</b> : jamais {">"} 5% du capital sur une seule position, quelle que soit la conviction.</li>
          <li><b>Diversification cross-asset</b> : mixer forex + crypto + indices + gold. Pas juste 8 paires forex.</li>
          <li><b>Options OTM comme hedge</b> : coût 1-2% par an pour couvrir un crash 20%. Standard hedge fund.</li>
          <li><b>Cash reserve permanente</b> : 30-50% du capital en cash. Les opportunités les plus juteuses arrivent après un crash.</li>
          <li><b>Kill-switch macro</b> : si VIX {">"} 40 ou drawdown journalier {">"} 5%, tout est fermé automatiquement (voir <Link to="/app/risk" className="text-accent-blue underline">Risk & Compliance</Link>).</li>
        </ol>
        <h3>Le calcul VaR + CVaR</h3>
        <p><b>VaR 95%</b> : perte maximale attendue dans 95% des cas (ex : « je ne perdrai pas plus de 3% dans 95% des sessions »). Utile mais insuffisant. <b>CVaR (Expected Shortfall)</b> : perte moyenne dans les 5% de pires cas. <b>C&apos;est la métrique institutionnelle standard.</b></p>
        <h3>Ce que ANATRADEX surveille</h3>
        <p>Chaque signal inclut un flag <code>tail_risk_score</code>. Si {">"} 0,7 = régime hors distribution normale (event risk imminent, ex : réunion FED, election US). Le signal est marqué <b>blocked=true</b> par précaution.</p>
      </>
    ),
  },
  {
    slug: "hedging-strategies-pratiques",
    track: "risk",
    level: "Pro",
    minutes: 6,
    order: 5,
    title: "Stratégies de hedge pratiques (sans options)",
    summary: "Comment couvrir un book long-only avec des paires FX inversement corrélées.",
    content: (
      <>
        <p>Pas besoin d&apos;options ou de contrats à terme pour hedger efficacement. Les corrélations forex offrent un hedge naturel.</p>
        <h3>1. Hedge FX inversement corrélé</h3>
        <p>Vous êtes long EURUSD (biais USD baissier). Pour hedger partiellement, ajoutez une position short USDCHF (corrélation -0,95 avec EURUSD). Une petite couverture 30-50% du notionnel principal réduit drastiquement la vol du portfolio.</p>
        <h3>2. Hedge risk-off via JPY / CHF / Gold</h3>
        <p>Si votre portfolio est long NASDAQ + long BTC (100% risk-on), ajoutez une petite exposition long JPY ou long Gold. Ces actifs montent quand tout le reste chute. <b>C&apos;est le « portfolio insurance » retail-friendly.</b></p>
        <h3>3. Hedge event-driven</h3>
        <p>Avant un événement majeur (FOMC, NFP), fermez ou hedgez les positions les plus exposées. Ne subissez pas passivement le pic de vol implicite.</p>
        <h3>4. Cash comme hedge</h3>
        <p>Souvent oublié : passer 40% cash = -40% de vol. Simple et efficace. Les fonds bull-market perma s&apos;auto-détruisent en 2008/2020 parce qu&apos;ils refusent le cash.</p>
        <h3>Coût du hedge</h3>
        <p>Un hedge parfait coûte cher. Un hedge partiel (30-50%) coûte peu et offre 70-80% de la protection. <b>Visez ça, pas la perfection.</b></p>
        <h3>Quand hedger</h3>
        <p>Systématiquement : jamais, ça détruit l&apos;edge. En réponse à un signal macro (VIX, VIX9D, credit spreads {">"} percentile 80) : oui, régulièrement.</p>
      </>
    ),
  },
];

// -----------------------------------------------------------------------------
// Track: Trading Journal + Systematic Trading
// -----------------------------------------------------------------------------
export const JOURNAL_ARTICLES = [
  {
    slug: "pourquoi-tenir-un-journal-de-trading",
    track: "journal",
    level: "Débutant",
    minutes: 5,
    order: 1,
    title: "Pourquoi tenir un journal de trading (et comment)",
    summary: "Sans journal, vous répétez les mêmes erreurs pendant 10 ans. Avec journal, vous progressez en 6 mois.",
    content: (
      <>
        <p>Le journal de trading est le <b>seul multiplicateur de progression</b> qui fonctionne pour tous les niveaux, du débutant au manager de fonds.</p>
        <h3>Ce qu&apos;un journal fait pour vous</h3>
        <ol>
          <li>Rend visibles vos <em>schémas récurrents</em> — les biais qu&apos;on ne voit pas seul.</li>
          <li>Sépare la stratégie de l&apos;exécution : la stratégie est-elle mauvaise ou juste mal appliquée ?</li>
          <li>Objective la performance — winrate réel, R:R réel, pas ressenti.</li>
          <li>Permet le calcul rigoureux du Sharpe, DD, expectancy.</li>
        </ol>
        <h3>Les 8 champs minimum</h3>
        <ul>
          <li>Date + heure d&apos;entrée / sortie</li>
          <li>Symbole + timeframe</li>
          <li>Direction, entrée, stop, cible</li>
          <li>Taille de position (lots + % du capital risqué)</li>
          <li>Justification en 1-2 lignes (setup, contexte régime)</li>
          <li>Émotion à l&apos;entrée (0-10 sur échelle de « calme » à « FOMO »)</li>
          <li>Screenshot avant + après</li>
          <li>Post-mortem : qu&apos;est-ce que je referais, qu&apos;est-ce que je changerais</li>
        </ul>
        <h3>Ce que ANATRADEX fait automatiquement</h3>
        <p>Le <Link to="/app/profile" className="text-accent-blue underline">Journal intégré</Link> capture automatiquement : timestamp, symbole, direction, taille, P&L à la sortie. Vous n&apos;avez que 3 champs à remplir : émotion, justification, post-mortem.</p>
        <h3>Fréquence de revue</h3>
        <ul>
          <li><b>Chaque soir</b> : 5 min, notes rapides</li>
          <li><b>Chaque vendredi</b> : synthèse hebdo, meilleurs / pires trades</li>
          <li><b>Chaque mois</b> : distribution winrate, R:R, DD, calcul Sharpe</li>
          <li><b>Chaque trimestre</b> : révision de stratégie — quelque chose doit changer si les stats stagnent</li>
        </ul>
      </>
    ),
  },
  {
    slug: "metriques-de-performance-cles",
    track: "journal",
    level: "Semi-pro",
    minutes: 7,
    order: 2,
    title: "Les 10 métriques qui comptent vraiment",
    summary: "Winrate, Sharpe, Sortino, Calmar, expectancy… Le tableau de bord d'un pro.",
    content: (
      <>
        <p>La P&L brute est un vanity metric. Les vraies métriques regardent la <b>qualité</b> de vos rendements.</p>
        <h3>Les métriques ratio (les plus importantes)</h3>
        <ol>
          <li><b>Sharpe Ratio</b> = (rendement - risk-free) / vol. Cible {">"} 1,5 en solo, {">"} 2 pour un LP.</li>
          <li><b>Sortino Ratio</b> = pareil mais uniquement la vol des <em>downside</em>. Plus juste car pénalise pas les grosses hausses.</li>
          <li><b>Calmar Ratio</b> = rendement annuel / max DD. Cible {">"} 1. Un Calmar de 3+ est excellent.</li>
          <li><b>Deflated Sharpe Ratio</b> = Sharpe corrigé du multiple testing. Voir <Link to="/academy/deflated-sharpe-explique" className="text-accent-blue underline">l&apos;article dédié</Link>.</li>
        </ol>
        <h3>Les métriques comportementales</h3>
        <ol start={5}>
          <li><b>Winrate</b> : % de trades gagnants. Contextuel — un winrate de 30% avec R:R 4 est meilleur que 70% avec R:R 0,5.</li>
          <li><b>R:R moyen</b> : gain moyen / perte moyenne. Cible {">"} 1,5. Si vous êtes à 0,8 vous êtes en train de couper vos gagnants.</li>
          <li><b>Expectancy</b> = (winrate × avg_win) - ((1-winrate) × avg_loss). En € par trade. Doit être {">"} 0.</li>
        </ol>
        <h3>Les métriques de risque</h3>
        <ol start={8}>
          <li><b>Max Drawdown</b> : la plus grosse perte pic-creux. À suivre religieusement.</li>
          <li><b>Ulcer Index</b> : pénalise la durée des DD, pas juste leur profondeur.</li>
          <li><b>VaR/CVaR</b> : perte attendue dans le 5% des pires jours.</li>
        </ol>
        <h3>Comment lire ces métriques ensemble</h3>
        <p>Un trader avec Sharpe 3 mais max DD 40% est <em>plus dangereux</em> qu&apos;un trader avec Sharpe 1,5 et max DD 8%. Toujours regarder <b>ratio + DD</b> ensemble.</p>
        <p><b>ANATRADEX affiche</b> Sharpe, Sortino, Calmar, DSR, max DD, Ulcer et VaR dans la page <Link to="/app/statistics" className="text-accent-blue underline">Statistical Suite</Link>, calculés en live sur vos trades journal.</p>
      </>
    ),
  },
  {
    slug: "trading-systematique-vs-discretionnaire",
    track: "journal",
    level: "Pro",
    minutes: 7,
    order: 3,
    title: "Trading systématique vs discrétionnaire : lequel choisir ?",
    summary: "Alpha, drawdown, scalabilité, résilience psychologique. Les vrais critères.",
    content: (
      <>
        <p>Le débat est vieux : trading systématique (algo, règles codées) ou discrétionnaire (jugement humain). En réalité, chacun a sa place — et ANATRADEX combine les deux.</p>
        <h3>Le trading systématique</h3>
        <p><b>Force :</b> discipline sans effort, backtesting rigoureux, scalabilité (peut gérer 1000 signaux/jour), pas d&apos;émotion.<br />
        <b>Faiblesse :</b> régime shift (une stratégie qui marche en trend meurt en range), overfitting facile, coût de l&apos;infrastructure, latence.</p>
        <h3>Le trading discrétionnaire</h3>
        <p><b>Force :</b> flexibilité, prise en compte du contexte (news, sentiment), meilleur en régimes bizarres.<br />
        <b>Faiblesse :</b> émotions, fatigue, non-reproductibilité, difficile à mesurer statistiquement.</p>
        <h3>Le sweet spot : hybride semi-systématique</h3>
        <p>Le meilleur setup pour un solo trader :</p>
        <ol>
          <li>Les <b>signaux</b> sont générés par algo (ex : ANATRADEX analyzer).</li>
          <li>L&apos;<b>exécution</b> est manuelle avec vérification contextuelle rapide.</li>
          <li>Le <b>sizing</b> est fixe (règles codées).</li>
          <li>Le <b>stop</b> et le <b>take profit</b> sont automatisés dès l&apos;entrée.</li>
        </ol>
        <p>Vous gagnez la discipline de l&apos;algo + la flexibilité humaine sur les cas edge.</p>
        <h3>Quand aller full-systématique</h3>
        <ul>
          <li>Vous avez validé {">"} 1000 trades avec ce système</li>
          <li>Le PBO est {"<"} 20%</li>
          <li>Le walk-forward tient sur 3+ périodes</li>
          <li>Vous avez du capital {">"} 50k € (les frais fixes deviennent supportables)</li>
        </ul>
        <h3>Quand rester discrétionnaire</h3>
        <ul>
          <li>Vous tradez des events macro (FED, NFP) où le contexte prime</li>
          <li>Vous êtes en apprentissage — vous devez développer l&apos;intuition d&apos;abord</li>
          <li>Vos volumes sont petits (moins de 30 trades/mois)</li>
        </ul>
      </>
    ),
  },
  {
    slug: "post-mortem-hebdomadaire",
    track: "journal",
    level: "Semi-pro",
    minutes: 5,
    order: 4,
    title: "Post-mortem hebdomadaire : le rituel qui change tout",
    summary: "30 min chaque vendredi. Le rituel qui distingue les traders qui progressent de ceux qui stagnent.",
    content: (
      <>
        <p>Le post-mortem hebdomadaire est le <b>compound interest de la progression</b>. 30 minutes / semaine × 52 semaines = 26 heures / an de méta-analyse. Voici la structure.</p>
        <h3>Étape 1 — Extraction des stats brutes (5 min)</h3>
        <ul>
          <li>Nombre de trades cette semaine</li>
          <li>Winrate, R:R, expectancy, Sharpe hebdo</li>
          <li>Max drawdown intra-semaine</li>
          <li>P&L nette (après frais / spread)</li>
        </ul>
        <p>ANATRADEX calcule tout ça automatiquement dans <Link to="/app/statistics" className="text-accent-blue underline">Statistical Suite</Link>.</p>
        <h3>Étape 2 — Identification des trades notables (10 min)</h3>
        <ul>
          <li><b>Le meilleur trade</b> : pourquoi a-t-il marché ? Setup répétable ?</li>
          <li><b>Le pire trade</b> : erreur d&apos;exécution ou de stratégie ?</li>
          <li><b>Le trade « raté »</b> (setup présent mais non pris) : pourquoi hésitation ?</li>
        </ul>
        <h3>Étape 3 — Analyse des schémas (10 min)</h3>
        <ul>
          <li>Est-ce que je perds toujours au même moment (Asian session, après NFP, en fin de journée) ?</li>
          <li>Est-ce que je perds toujours sur le même actif ?</li>
          <li>Est-ce que mes émotions étaient dégradées (dormir mal, stress hors marchés) ?</li>
          <li>Est-ce que le régime marché a changé ?</li>
        </ul>
        <h3>Étape 4 — Décisions actionnables (5 min)</h3>
        <ul>
          <li>1 chose à faire différemment la semaine prochaine (une seule, pas dix).</li>
          <li>1 setup à renforcer.</li>
          <li>1 setup à retirer temporairement du plan.</li>
        </ul>
        <h3>Étape 5 — Archivage</h3>
        <p>Notez la synthèse dans un fichier persistant (Notion, Obsidian, ou le journal ANATRADEX). Relisez le mois précédent avant de commencer. <b>Les patterns émergent sur 3-6 mois</b>, pas sur une semaine.</p>
        <p><b>La règle d&apos;or :</b> ce rituel doit être <em>non négociable</em>. Même si vous êtes en vacances. 30 min de post-mortem = 10× la valeur de 30 min de trading supplémentaire.</p>
      </>
    ),
  },
];

// Aggregate export used by Academy.jsx to merge all extra tracks into ALL_ARTICLES.
export const EXTRA_TRACKS_ARTICLES = [
  ...PSYCHOLOGY_ARTICLES,
  ...RISK_ARTICLES,
  ...JOURNAL_ARTICLES,
];

export const EXTRA_TRACKS_META = [
  {
    id: "psychology",
    label: "Psychologie du trading",
    emoji: "🧠",
    articles: PSYCHOLOGY_ARTICLES,
    intro: "4 articles progressifs sur les biais cognitifs, le tilt, la routine et la traversée des drawdowns.",
    activeClass: "bg-rose-500/20 border-rose-500/50 text-rose-300",
    introClass: "bg-rose-500/5 border-rose-500/20",
    introTitleClass: "text-rose-300",
  },
  {
    id: "risk",
    label: "Gestion du risque avancée",
    emoji: "🛡️",
    articles: RISK_ARTICLES,
    intro: "5 articles sur le sizing, les corrélations, les stops avancés, le tail risk et le hedging pratique.",
    activeClass: "bg-amber-500/20 border-amber-500/50 text-amber-300",
    introClass: "bg-amber-500/5 border-amber-500/20",
    introTitleClass: "text-amber-300",
  },
  {
    id: "journal",
    label: "Journal & Systématique",
    emoji: "📓",
    articles: JOURNAL_ARTICLES,
    intro: "4 articles sur le journal, les métriques clés, le débat systématique vs discrétionnaire et le post-mortem hebdomadaire.",
    activeClass: "bg-cyan-500/20 border-cyan-500/50 text-cyan-300",
    introClass: "bg-cyan-500/5 border-cyan-500/20",
    introTitleClass: "text-cyan-300",
  },
];
